/**
 * studio/src/lib/appwrite.ts
 *
 * Appwrite Web SDK v1.9.x client for the creator dashboard SPA.
 * Uses: Client, Account, Databases, Storage, Functions, Query, ID, Permission, Role.
 *
 * Setup: Add a Web platform in Appwrite Console for studio.honestpitches.com
 * before using in production.
 */

import { aggregateFromRatings } from "@honest-pitches/honestscore-core";
import { reportError } from "./sentry-bootstrap";
import { rawErrorMessage, toUserFacingError } from "./user-facing-error";

import {
  Client,
  Account,
  Databases,
  Storage,
  Functions,
  ExecutionMethod,
  Query,
  ID,
  Permission,
  Role,
  type Models,
} from "appwrite";

// ─── Client singleton ──────────────────────────────────────────────────────────

export const client = new Client();

const endpoint = (import.meta.env.VITE_APPWRITE_ENDPOINT as string) ?? "http://localhost/v1";
const projectId = (import.meta.env.VITE_APPWRITE_PROJECT_ID as string) ?? "";
const databaseId = (import.meta.env.VITE_APPWRITE_DATABASE_ID as string) ?? "honest_pitches";

client.setEndpoint(endpoint).setProject(projectId);

// ─── Service exports ─────────────────────────────────────────────────────────

export const account = new Account(client);
export const databases = new Databases(client);
export const storage = new Storage(client);
export const functionsSvc = new Functions(client);

// ─── Database ID ─────────────────────────────────────────────────────────────

export { databaseId };

// ─── Collection IDs ───────────────────────────────────────────────────────────

export const COLLS = {
  creators: "creators",
  pitches: "pitches",
  pitch_assets: "pitch_assets",
  pitch_listings: "pitch_listings",
  pitch_sections: "pitch_sections",
  pitch_audience_questions: "pitch_audience_questions",
  pitch_audience_answers: "pitch_audience_answers",
  pitch_disclosures: "pitch_disclosures",
  pitch_pros_cons: "pitch_pros_cons",
  pitch_videos: "pitch_videos",
  claims: "claims",
  votes_hype_reality: "votes_hype_reality",
  flags: "flags",
  conversion_events: "conversion_events",
  subscriptions: "subscriptions",
  audit_log: "audit_log",
  stripe_events: "stripe_events",
  build_jobs: "build_jobs",
  legacy_creator_redirects: "legacy_creator_redirects",
  ratings_honestscore: "ratings_honestscore",
  ratings_honestscore_cache: "ratings_honestscore_cache",
} as const;

// ─── Auth ───────────────────────────────────────────────────────────────────

/**
 * Send a magic link to the user's email.
 * After they click the link, use createSessionFromSecret() to log in.
 */
export async function sendMagicLink(email: string, redirectUrl: string) {
  return account.createMagicURLToken(ID.unique(), email, redirectUrl);
}

/**
 * Complete magic-link login using the secret from the URL query params.
 *
 * Uses `account.updateMagicURLSession(userId, secret)` — the Appwrite 1.9.5
 * dedicated magic-URL endpoint (PUT `/account/sessions/magic-url`), NOT
 * the legacy unified `account.createSession` (POST `/account/sessions/token`).
 *
 * Why (2026-06-30 bug + fix): Appwrite 1.9.5's `createSession` endpoint
 * rejects magic-URL-shaped tokens with `Invalid token passed in the request`.
 * Symptom: clicking the magic-link email renders the "Sign in failed"
 * callback page on the FIRST attempt; a page refresh happens to recover
 * because a stale session cookie + the AuthCallback early-bail routes
 * the user without re-consuming the token. Per the official Account API
 * reference, `updateMagicURLSession` is the magic-URL-specific method.
 * Pinned by `auth-magic-link.contract.test.ts`.
 */
export async function createSessionFromSecret(userId: string, secret: string) {
  return account.updateMagicURLSession(userId, secret);
}

export async function getCurrentUser(): Promise<Models.User<Record<string, unknown>>> {
  return account.get();
}

export async function deleteCurrentSession() {
  return account.deleteSession("current");
}

export async function listUserSessions() {
  return account.listSessions();
}

// ─── Pitches ─────────────────────────────────────────────────────────────────

export async function listPitchesForCreator(
  userId: string,
  status?: string,
  /**
   * 2026-06-28 (deletion safety): visibility filter for the
   * dashboard tabs.
   *
   * - `undefined` (default) → active tab: explicit
   *   `notEqual('visibility', 'archived')` so creators do not
   *   see archived pitches on the active dashboard tab.
   *   Mirrors `pitches-public-feed`'s exclusion of archived
   *   rows from public listings.
   * - `'archived'` → archived tab: `Query.equal('visibility', 'archived')`.
   * - `'all'` → no visibility filter (escape hatch for
   *   future reports that want both active + archived rows).
   */
  visibility?: "all" | "archived",
) {
  // founder/creator ownership: `pitches.creatorId` is a `creators.$id`
  // (joined from `auth.userId` via `getCreatorByUserId(auth.userId).$id`
  // in callers).
  const queries = [Query.equal("creatorId", userId)];
  if (status) queries.push(Query.equal("status", status));
  if (visibility === "archived") {
    queries.push(Query.equal("visibility", "archived"));
  } else if (visibility === undefined) {
    queries.push(Query.notEqual("visibility", "archived"));
  }
  return databases.listDocuments(databaseId, COLLS.pitches, queries);
}

export async function getPitch(pitchId: string) {
  return databases.getDocument(databaseId, COLLS.pitches, pitchId);
}

export async function getPitchBySlug(slug: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitches, [
    Query.equal("slug", slug),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function getPitchByHpCode(hpCode: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitches, [
    Query.equal("hpCode", hpCode),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

/** Current canonical pitch for an HP code (not superseded by a newer replacement). */
export async function getCanonicalPitchByHpCode(hpCode: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitches, [
    Query.equal("hpCode", hpCode),
    Query.isNull("replacedByPitchId"),
    Query.orderDesc("$createdAt"),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function createPitch(data: Record<string, unknown>) {
  // 2026-06-25 fix: the previous shape passed `Role.user(<creatorId>)`
  // (a creators.$id) as the per-row update/delete permission. The
  // Appwrite 1.9.5 server rejected that with "Permissions must be one
  // of: (any, users, user:<caller-userId>, user:<caller-userId>/verified,
  // users/verified)" — the v16 client SDK can only grant permissions
  // to the caller or to roles, never to an arbitrary other user (per
  // https://appwrite.io/docs/references/cloud/client-web/databases —
  // "When using Client SDKs, you can only grant permissions to yourself
  // or roles you belong to").
  //
  // The `pitches` table has `rowSecurity: false` and `$permissions`:
  //   read("any"), create("users"), update("users"), delete("users")
  // so the v16 client SDK's default behavior — "the creator
  // automatically gets read/update/delete on the row they just wrote"
  // — is exactly what we want. Drop the explicit permissions arg and
  // let the SDK apply its default. See
  // `appwrite.rowSecurity.test.ts` for the regression pin.
  return databases.createDocument(
    databaseId,
    COLLS.pitches,
    ID.unique(),
    data
  );
}

/** Shared write path for the pitch-write Appwrite Function. */
export const PITCH_WRITE_FUNCTION_ID = "pitch-write";

/** Shared asset upload path — see docs-new/assets-cdn.md. */
export const PITCH_ASSET_UPLOAD_FUNCTION_ID = "pitch-asset-upload";

/**
 * 2026-06-26: three-step presign flow for pitch assets (mirrors the
 * bunny-presign-upload pattern used for videos).
 *
 * The flow:
 *   1. `pitch-asset-presign`  — server validates ownership, plans the
 *      Bunny path, and signs a short-lived PUT URL.
 *   2. Browser PUTs the bytes directly to Bunny Storage via XHR so
 *      `xhr.upload.onprogress` fires real percentages.
 *   3. `pitch-asset-finalize` — server probes Bunny to confirm the
 *      object landed, then writes the `pitch_assets` row + audit log.
 *
 * The `pitch-asset-upload` function above is kept for one deploy
 * cycle of overlap (e.g. for the `pnpm regen:og-thumbnails` CLI
 * which prefers a single-call fallback) and is marked deprecated
 * for new browser callers.
 */
export const PITCH_ASSET_PRESIGN_FUNCTION_ID = "pitch-asset-presign";
export const PITCH_ASSET_FINALIZE_FUNCTION_ID = "pitch-asset-finalize";

/** Bunny Stream presign (TUS signature) — see bunny-presign-upload/src. */
export const BUNNY_PRESIGN_UPLOAD_FUNCTION_ID = "bunny-presign-upload";

/** Asset kinds accepted by the pitch-asset-upload function.
 *
 * Source of truth is the `ASSET_KINDS` runtime array in
 * `@honest-pitches/pitch-asset-core`. The studio only exposes a
 * narrowed editor-facing set (see `EDITOR_ASSET_KINDS` below);
 * `bunny_video` and `attachment` are deferred (Bunny Stream is a
 * separate plan; attachments have no template consumer). The
 * `PitchAssetKind` alias is kept for any external callers of the
 * studio (e.g. the marketing copy review skill) and is the full
 * server-side set.
 *
 * Pinned by `studio/src/lib/appwrite.contract.test.ts`.
 */
import { ASSET_KINDS, getKindLimit, type AssetKind, type AssetKindLimit } from "@honest-pitches/pitch-asset-core";
import {
  canHardDeleteFromRow,
  isHardDeletableFromRow,
} from "@honest-pitches/pitch-core/browser";
// The studio runs in a Vite browser bundle; the default subpath of
// @honest-pitches/pitch-core resolves to the Node-ESM build
// (dist/index.node.mjs, which prepends a `createRequire(import.meta.url)`
// banner to satisfy dynamic `__require("fs") / __require("path")` calls
// from the inlined `sanitize-html` / `marked` / `json5` deps). Vite
// externalizes Node built-ins, so importing the default subpath here
// crashed the studio build with
//   "createRequire" is not exported by "__vite-browser-external"
// The /browser subpath resolves to the clean ESM build
// (dist/index.js) that has no Node-only banner. See
// packages/pitch-core/src/build-artifacts.test.ts for the contract.

export type PitchAssetKind = AssetKind;
export type { AssetKind };
export { getKindLimit, type AssetKindLimit };

/**
 * Editor-facing asset kinds — the subset of `ASSET_KINDS` that the
 * studio UI knows how to upload, replace, and remove. Excludes
 * `bunny_video` (Bunny Stream, follow-up plan) and `attachment`
 * (no template consumer). New kinds added to the core's
 * `ASSET_KINDS` will not appear here automatically; if a new kind
 * should be in the editor, add it explicitly.
 *
 * 2026-06-25 (unify_image_picker plan): `"avatar"` was added as
 * the **account-level** asset kind. It is NOT a `PitchAssetKind`
 * (it does not route through `pitch-asset-upload` or `pitch_assets`
 * — avatars live in the Appwrite Storage `user-media` bucket and
 * are listed by the `my-avatars-list` Appwrite Function). The
 * `"avatar"` value is therefore typed as a literal here, not via
 * `AssetKind`, so the studio `EditorAssetKind` union includes it
 * for the picker without dragging the avatar through the
 * per-pitch validation.
 */
export const EDITOR_ASSET_KINDS = [
  "thumbnail",
  "video_poster",
  "og_image",
  "segment_image",
  // 2026-06-28: per-section video uploads (Issue #4). Uses the
  // existing `uploadPitchAssetViaPresign` flow (no Bunny Stream
  // detour). The picked asset's $id is written into
  // `pitch_sections.bunnyVideoId` so the section renderer can
  // join on it. The schema column is operator-pushed per the
  // standing `operator-pushes-schema` rule.
  "segment_video",
  // 2026-06-28 (Issue #2): pitch-level favicon + logo. Picked
  // from the Media step via the shared MediaPickerModal, written
  // into `pitches.faviconFileId` + `pitches.logoFileId` (both
  // columns operator-pushed per the standing
  // `operator-pushes-schema` rule).
  "favicon",
  "logo",
] as const satisfies readonly AssetKind[];

/** Editor + account-level asset kinds accepted by `MediaPickerModal`. */
export type EditorAssetKind = (typeof EDITOR_ASSET_KINDS)[number] | "avatar";

/**
 * True for the account-level avatar kind, which has no `pitchId`
 * and uses Appwrite Storage (not `pitch_assets`) for both the
 * upload path and the library listing.
 */
export function isAvatarKind(kind: EditorAssetKind): kind is "avatar" {
  return kind === "avatar";
}

/** @deprecated use PITCH_WRITE_FUNCTION_ID */
export const PITCH_CREATE_FUNCTION_ID = PITCH_WRITE_FUNCTION_ID;

export const PITCH_TRANSITION_FUNCTION_ID = "pitch-transition";

/** Pitcher per-user API key issuer (Authorization: Bearer hp_pk_…). */
export const PITCHER_API_KEY_ISSUE_FUNCTION_ID = "pitcher-api-key-issue";

/** Audience-feedback write path — POST /questions and POST /answers. */
export const AUDIENCE_FEEDBACK_WRITE_FUNCTION_ID = "audience-feedback-write";

/** Audience-feedback read path — pitcher dashboard rollups. */
export const AUDIENCE_FEEDBACK_PUBLIC_FUNCTION_ID = "audience-feedback-public";

/**
 * 2026-06-28 (studio dashboard aggregate): the owner-gated read
 * endpoint for a single pitch's HonestScore + critic count.
 * Routes:
 *   GET /aggregate/:pitchId
 * The endpoint lives on the `pitch-rate` function (NOT on the
 * public feed) because the gate is the pitch creator's identity:
 * the same `pitch-rate` function serves public widget submissions,
 * so row-level write permissions can't express "owner-scoped read
 * of the same tables" — the owner gate has to live in the function
 * itself. See `pitch-rate/src/handler.ts#handleGetAggregate`.
 */
export const PITCH_RATE_FUNCTION_ID = "pitch-rate";

/** Pitch Sharing Zone write path — record a share event, send email, manage tips. */
export const PITCH_SHARE_WRITE_FUNCTION_ID = "pitch-share-write";

/** Pitch Sharing Zone QR generator — server-side QR PNG for a pitch. */
export const PITCH_SHARE_QR_FUNCTION_ID = "pitch-share-qr";

/**
 * Account-level "My Media" library — pitch_assets rows across the
 * caller's pitches (segment images, OG images, thumbnails, video
 * posters, attachments, Bunny videos). Routes:
 *   GET  /assets?kind=...&limit=...&offset=...
 * Returns `ListMyMediaResponse` (see `src/lib/media.ts`).
 */
export const MEDIA_ASSET_LIBRARY_FUNCTION_ID = "media-asset-library";

/**
 * Account-level "My Media" delete — owner-scoped, in-use guarded.
 * Routes:
 *   POST /assets/:assetId   (alias for DELETE; see dispatch docstring)
 */
export const MEDIA_ASSET_DELETE_FUNCTION_ID = "media-asset-delete";

/**
 * Account-level "My Videos" library — pitch_videos rows owned by the
 * caller, paginated. Routes:
 *   GET  /videos?status=ready|failed|uploading&limit=...&offset=...
 * Returns `ListMyVideosResponse` (see `src/views/MyVideosView.vue`).
 */
export const MY_VIDEOS_LIST_FUNCTION_ID = "my-videos-list";

/**
 * Account-level "My Videos" attach — set a picked video as a
 * pitch's `primaryVideoId`. Routes:
 *   POST /attach   body: { videoId: string, pitchId: string }
 */
export const MY_VIDEOS_ATTACH_FUNCTION_ID = "my-videos-attach";

/**
 * Account-level "My Videos" delete — owner-scoped, in-use guarded
 * (2026-06-30). Mirrors `media-asset-delete` for the video side: a
 * server-side function that verifies the caller owns the
 * `pitch_videos` row via pitch → creator → userId, then refuses
 * with 403 + `blockingPitchIds` if any live+public pitch still
 * references the video (hero via `pitches.primaryVideoId`, or
 * section via `pitch_sections.bunnyVideoId`). On the happy path
 * it deletes the Bunny Stream video first (idempotent), then the
 * `pitch_videos` row (which also fires the event-driven
 * `bunny-video-delete` for a no-op double-cleanup).
 *
 * Routes:
 *   POST   /videos/:videoId    (alias for DELETE; some HTTP clients
 *                                can't send DELETE with body)
 *   DELETE /videos/:videoId
 */
export const MY_VIDEOS_DELETE_FUNCTION_ID = "my-videos-delete";

/** Consolidated Appwrite function bundles (2026-07). */
export const STUDIO_API_FUNCTION_ID = "honest-pitches-studio-api";
export const MEDIA_API_FUNCTION_ID = "honest-pitches-media";
export const PUBLIC_API_FUNCTION_ID = "honest-pitches-public-api";
export const STRIPE_API_FUNCTION_ID = "stripe-api";

/**
 * Maps legacy per-route function IDs to consolidated bundle + path prefix.
 * invokeFunction() rewrites functionId/path before createExecution.
 */
const FUNCTION_BUNDLE_MAP: Record<string, string> = {
  [PITCH_WRITE_FUNCTION_ID]: STUDIO_API_FUNCTION_ID,
  [PITCH_TRANSITION_FUNCTION_ID]: STUDIO_API_FUNCTION_ID,
  [PITCHER_API_KEY_ISSUE_FUNCTION_ID]: STUDIO_API_FUNCTION_ID,
  "pitch-og-generate": STUDIO_API_FUNCTION_ID,
  [PITCH_ASSET_UPLOAD_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [PITCH_ASSET_PRESIGN_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [PITCH_ASSET_FINALIZE_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [BUNNY_PRESIGN_UPLOAD_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [MEDIA_ASSET_LIBRARY_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [MEDIA_ASSET_DELETE_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [MY_VIDEOS_LIST_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [MY_VIDEOS_ATTACH_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  [MY_VIDEOS_DELETE_FUNCTION_ID]: MEDIA_API_FUNCTION_ID,
  "pitch-video-section-attach": MEDIA_API_FUNCTION_ID,
  [AUDIENCE_FEEDBACK_WRITE_FUNCTION_ID]: PUBLIC_API_FUNCTION_ID,
  [AUDIENCE_FEEDBACK_PUBLIC_FUNCTION_ID]: PUBLIC_API_FUNCTION_ID,
  [PITCH_RATE_FUNCTION_ID]: PUBLIC_API_FUNCTION_ID,
  [PITCH_SHARE_WRITE_FUNCTION_ID]: PUBLIC_API_FUNCTION_ID,
  [PITCH_SHARE_QR_FUNCTION_ID]: PUBLIC_API_FUNCTION_ID,
  "stripe-checkout": STRIPE_API_FUNCTION_ID,
  "stripe-checkout-waitlist": STRIPE_API_FUNCTION_ID,
};

function resolveConsolidatedRoute(
  functionId: string,
  path: string,
): { functionId: string; path: string } {
  const bundle = FUNCTION_BUNDLE_MAP[functionId];
  if (!bundle) return { functionId, path };
  const sub = path.startsWith("/") ? path : `/${path}`;
  const prefixed = sub === "/" ? `/${functionId}` : `/${functionId}${sub}`;
  return { functionId: bundle, path: prefixed };
}

/** Bundles whose dispatchers call `requireStudioUser` (body.userId). */
const STUDIO_AUTHENTICATED_BUNDLES = new Set([
  STUDIO_API_FUNCTION_ID,
  MEDIA_API_FUNCTION_ID,
]);

/**
 * Consolidated studio/media routes authenticate via
 * `requireStudioUser`, which reads `userId` from the JSON body
 * because Appwrite 1.9.5 blocks browser clients from sending
 * `x-appwrite-user-id`. Dynamic-import auth to avoid a static
 * circular dependency (`auth.ts` imports this module).
 */
async function studioUserIdForInvoke(): Promise<string | null> {
  try {
    const { useAuthStore } = await import("./auth");
    return useAuthStore().userId;
  } catch {
    return null;
  }
}

async function bodyForInvoke(
  functionId: string,
  path: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const resolved = resolveConsolidatedRoute(functionId, path);
  if (!STUDIO_AUTHENTICATED_BUNDLES.has(resolved.functionId)) {
    return body;
  }
  const existing =
    (typeof body.userId === "string" && body.userId.length > 0 && body.userId) ||
    (typeof body.user_id === "string" && body.user_id.length > 0 && body.user_id) ||
    null;
  if (existing) return body;
  const userId = await studioUserIdForInvoke();
  if (!userId) return body;
  return { ...body, userId };
}

/**
 * Invoke an Appwrite Function synchronously via the SDK.
 *
 * Exported so callers outside `appwrite.ts` (e.g. `bunny-presign.ts`)
 * can hit a non-pitch function via the same SDK path. The browser
 * preflight only carries `x-sdk-*` headers and the session cookie
 * (via `credentials: 'include'`), so this avoids the Appwrite 1.9.5
 * CORS issue where a manual `fetch()` with custom headers like
 * `x-appwrite-user-id` is rejected at the preflight.
 */
export async function invokeFunction<T = Record<string, unknown>>(
  functionId: string,
  body: Record<string, unknown>,
  path = "/",
  method: ExecutionMethod = ExecutionMethod.POST,
): Promise<T> {
  const resolved = resolveConsolidatedRoute(functionId, path);
  const payload = await bodyForInvoke(functionId, path, body);
  let execution: {
    status?: string;
    responseStatusCode?: number;
    duration?: number;
    responseBody?: string;
    errors?: string;
  };
  try {
    execution = await functionsSvc.createExecution(
      resolved.functionId,
      JSON.stringify(payload),
      false,
      resolved.path,
      method,
      { "content-type": "application/json" },
    );
  } catch (sdkErr: unknown) {
    const raw = rawErrorMessage(sdkErr);
    const ufe = toUserFacingError(sdkErr);
    const err = new Error(raw);
    (err as FunctionInvokeError).userMessage = ufe.headline;
    if (ufe.detail) {
      (err as FunctionInvokeError).technicalDetail = ufe.detail;
    }
    reportError(err, {
      functionId,
      path: resolved.path,
      method,
      kind: "network-fetch",
    });
    throw err;
  }

  const execStatus = String((execution as { status?: string }).status ?? "");
  const httpStatus = execution.responseStatusCode ?? 0;
  const durationMs =
    typeof (execution as { duration?: number }).duration === "number"
      ? (execution as { duration: number }).duration
      : undefined;

  const responseBodyText =
    typeof execution.responseBody === "string" ? execution.responseBody.trim() : "";
  const errorBody = safeJson(execution.responseBody ?? "") as { error?: string } | null;

  function executionErrorMessage(): string {
    if (errorBody?.error) return errorBody.error;
    const errors = execution.errors?.trim();
    if (errors) return errors.replace(/^ERROR \| /, "");
    if (responseBodyText && !responseBodyText.startsWith("{")) {
      return responseBodyText.replace(/^ERROR \| /, "");
    }
    if (execStatus === "failed" && httpStatus >= 500) {
      return "Execution timed out";
    }
    return `${functionId} failed`;
  }

  if (execStatus === "failed" || httpStatus >= 400) {
    const rawMessage = executionErrorMessage();
    const err = new Error(rawMessage);
    // 2026-07-01 (operator "what's the problem creating a new pitch?"):
    // When the Appwrite 1.9.5 TablesDB rejects the createRow, the
    // function's catch returns the raw error string, which carries
    // either an enum-dump
    // (`Attribute "framework" has invalid format. Value must be
    // one of (...)`) or a missing-attribute list
    // (`Missing required attribute "X"`). Both are operator-side
    // diagnostic noise that no wizard user can act on — they look
    // like "the server misconfigured itself" instead of "the
    // value the wizard sent isn't accepted by the backend schema
    // yet." Decorate the error with a friendlier `userMessage`
    // the wizard toasts can show, plus `errorKind` /
    // `offendingAttribute` for the GlitchTip side so an operator
    // can bisect the failure without the raw enum dump. The
    // contract test in `appwrite.errors.test.ts` pins both shapes.
    const schemaMatch = parseTablesDBSchemaError(rawMessage);
    if (schemaMatch) {
      // 2026-07-01 (operator "what's the problem creating a new pitch?"):
      // attach the friendly shape to the Error via a typed shape so the
      // wizard's toast and the GlitchTip side can read them without a
      // stringly-typed `any` cast. The contract test in
      // `appwrite.errors.test.ts` reads `error.errorKind` / `.offendingAttribute`
      // / `.userMessage` off the thrown error to pin the decoration.
      (err as FunctionInvokeError).errorKind = schemaMatch.kind;
      (err as FunctionInvokeError).offendingAttribute = schemaMatch.attribute;
      (err as FunctionInvokeError).userMessage = schemaMatch.userMessage;
    }
    const looksTimedOut =
      /timed out/i.test(rawMessage) ||
      (execStatus === "failed" && httpStatus >= 500 && (durationMs ?? 0) >= 25_000);
    if (looksTimedOut) {
      (err as FunctionInvokeError).userMessage =
        "The server took too long to save your video choice. Please try again.";
    }
    // 2026-06-29 (operator-requested debug): `Error: pitch not found`
    // is the entire payload the UI sees — no hint about the function
    // id, the HTTP status, the path that was called, or the full
    // response body. Log everything here so an operator can bisect
    // `pitch not found` (404 ownership miss) vs `Server misconfigured`
    // (500 env-var gap) vs `ownership lookup failed: …` (500 db
    // failure) without re-running with the network panel open.
    // eslint-disable-next-line no-console
    console.error("[invokeFunction] failed", {
      functionId,
      path: resolved.path,
      method,
      httpStatus: execution.responseStatusCode,
      execStatus,
      requestBody: payload,
      responseBody: execution.responseBody,
      errors: execution.errors,
      parsedError: errorBody?.error ?? null,
      errorKind: schemaMatch?.kind,
      offendingAttribute: schemaMatch?.attribute,
    });
    // 2026-06-24: forward to GlitchTip so every failed function
    // execution is visible. Tagged with the function id + HTTP
    // status so per-function failures group separately. See
    // src/lib/sentry-bootstrap.ts for the no-op-when-DSN-is-unset
    // contract — when DSN is unset this is a free no-op.
    reportError(err, {
      functionId,
      httpStatus: execution.responseStatusCode,
      path: resolved.path,
      method,
      execStatus,
      errorKind: schemaMatch?.kind,
      offendingAttribute: schemaMatch?.attribute,
    });
    throw err;
  }
  return (safeJson(execution.responseBody ?? "") ?? {}) as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Typed shape of the Error thrown by `invokeFunction` when the
 * Appwrite 1.9.5 TablesDB returns a schema mismatch. The wizard's
 * `mintDraft` (and every other future consumer of `invokeFunction`)
 * reads `error.userMessage` over `error.message` for the
 * user-facing toast, and the GlitchTip side reads `errorKind` +
 * `offendingAttribute` for bisection. Non-schema errors omit all
 * three fields — the caller falls back to `err.message`.
 */
interface FunctionInvokeError extends Error {
  errorKind?: "schema-enum-mismatch" | "schema-missing-required";
  offendingAttribute?: string;
  userMessage?: string;
  /** Raw SDK / network message when `userMessage` is the friendly headline. */
  technicalDetail?: string;
}

/**
 * 2026-07-01 (operator "what's the problem creating a new pitch?"):
 * Parse Appwrite 1.9.5 self-hosted TablesDB validation errors into
 * a friendlier shape for the wizard's user-facing toast. Two cases:
 *
 *  - `Attribute "X" has invalid format. Value must be one of (…)`
 *    → `errorKind: "schema-enum-mismatch"`, `attribute: "X"`. The
 *    wizard toasts "That pitch configuration doesn't match what
 *    the server's schema currently allows. Try a different option,
 *    or contact support if the problem persists." and the operator
 *    bisects via the GlitchTip `errorKind="schema-enum-mismatch"`
 *    + `offendingAttribute="X"` pair.
 *
 *  - `Missing required attribute "X"` → `errorKind:
 *    "schema-missing-required"`, `attribute: "X"`. The wizard
 *    toasts "Some required fields are missing — please fill them
 *    in and try again." The operator knows to inspect the field
 *    the payload dropped.
 *
 * Returns `null` for any non-schema error shape (e.g. `Server
 * misconfigured`, `ownership lookup failed: …`) — the caller falls
 * back to the raw `errors` string.
 */
function parseTablesDBSchemaError(
  raw: string,
): { kind: "schema-enum-mismatch" | "schema-missing-required"; attribute: string; userMessage: string } | null {
  // TablesDB enum-mismatch:
  //   'Attribute "framework" has invalid format. Value must be one of (...)'
  const enumMatch = raw.match(/Attribute "([^"]+)" has invalid format/);
  if (enumMatch && enumMatch[1]) {
    const attribute = enumMatch[1];
    return {
      kind: "schema-enum-mismatch",
      attribute,
      userMessage:
        `The server's "${attribute}" option doesn't match anything this wizard can send ` +
        `(this is a backend schema drift — please contact support, or try a different ` +
        `framework if the "${attribute}" list is visible above).`,
    };
  }
  // TablesDB missing-required:
  //   'Missing required attribute "creatorId"'
  const missingMatch = raw.match(/Missing required attribute "([^"]+)"/);
  if (missingMatch && missingMatch[1]) {
    const attribute = missingMatch[1];
    return {
      kind: "schema-missing-required",
      attribute,
      userMessage:
        `The wizard left a required "${attribute}" field empty. Please go back a step ` +
        `and fill it in, then try saving again.`,
    };
  }
  return null;
}

/** Create a pitch via pitch-write (POST /). */
export async function createPitchViaFunction(data: Record<string, unknown>) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, data);
}

/** Update a pitch via pitch-write (PATCH /:id). */
export async function updatePitchViaFunction(
  pitchId: string,
  data: Record<string, unknown>,
) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, data, `/${pitchId}`, ExecutionMethod.PATCH);
}

// 2026-06-21 (clean-break): `flipPitchToTest` is removed — the
// test-mode prototype affordance is gone. The corresponding
// `POST /:id/test` route in `pitch-write` (and its
// `handleFlipToTest` handler) is also deleted.

/** Start paid publish flow (POST /:id/pay). */
export async function flipPitchToPayment(pitchId: string) {
  return invokeFunction(PITCH_WRITE_FUNCTION_ID, {}, `/${pitchId}/pay`);
}

/** Promote a replacement via pitch-transition. */
export async function promotePitchReplacement(
  replacementId: string,
  predecessorId: string,
) {
  return invokeFunction(
    PITCH_TRANSITION_FUNCTION_ID,
    { predecessorId },
    `/${replacementId}/promote-replacement`,
  );
}

/** Archive a pitch via pitch-transition. */
export async function archivePitchViaFunction(pitchId: string) {
  return invokeFunction(
    PITCH_TRANSITION_FUNCTION_ID,
    {},
    `/${pitchId}/archive`,
  );
}

/** Reactivate an archived pitch via pitch-transition. */
export async function reactivatePitchViaFunction(pitchId: string) {
  return invokeFunction(
    PITCH_TRANSITION_FUNCTION_ID,
    {},
    `/${pitchId}/reactivate`,
  );
}

/** Create a Stripe checkout session via the stripe-checkout Appwrite Function. */
export async function createStripeCheckout(
  data: Record<string, unknown>,
): Promise<{ url: string | null }> {
  return (await invokeFunction("stripe-checkout", data)) as { url: string | null };
}

export async function updatePitch(
  pitchId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.pitches, pitchId, data);
}

export async function deletePitch(pitchId: string) {
  return databases.deleteDocument(databaseId, COLLS.pitches, pitchId);
}

/**
 * Hard-delete a pitch row.
 *
 * 2026-06-28 (deletion safety): this function is the single
 * hard-delete primitive for the studio. The precondition is the
 * "is-this-row-deletable" gate, NOT a "must-already-be-archived"
 * gate (the previous name `purgeArchivedPitch` was misleading —
 * drafts are deletable too).
 *
 * Deletable states (deleteDocument fires):
 *   - `status === "draft"` (or `pending_payment`, which resolves
 *     to draft — see `@honest-pitches/pitch-core#canHardDeleteFromRow`).
 *     The creator's pre-publish workspace row. No Bunny
 *     side-effects to clean up.
 *   - `visibility === "archived"` — the row has already
 *     flowed through `pitch-transition.handleArchive`'s cascade,
 *     so Bunny Stream + Storage cleanup is done.
 *
 * Blocked states (throws):
 *   - `preview`, `live`, `expired` — these must be archived
 *     first via the Lifecycle panel's Archive button (which is
 *     what fires the cascade). A UI-gate bypass that calls this
 *     function against a live row surfaces immediately rather
 *     than silently hard-deleting a pitch with active
 *     Bunny Stream/Storage assets.
 *
 * 2026-06-29 (regression pin): the precondition is delegated to
 * the row-level helpers `canHardDeleteFromRow(row)` +
 * `isHardDeletableFromRow(row)` from `@honest-pitches/pitch-core`.
 * Routing through `canHardDeleteFromLifecycle(lifecycleFromRow(row))`
 * (the *lifecycle*-state helper) classified `{ status: "draft",
 * previewToken: "..." }` as `"preview"` — every real-world
 * studio-minted draft has a `previewToken` (the wizard mints one
 * on draft creation; see `PitchWizardV2.vue`, `PitchEdit.vue`,
 * `PitchReplace.vue`, `PitchWizard.vue`), so the gate used to
 * throw `"Pitch must be archived before it can be deleted."`
 * on every draft click. The row-level helpers read the row's raw
 * `status` column directly so the destructive-action gate
 * classifies drafts by their `status`, not by their
 * preview-URL affordance. They MUST agree with
 * `useEditablePitch#canDeleteWithoutArchive`/`isHardDeletable`,
 * which this commit also routes through the same row-level
 * helpers — see `src/lib/use-editable-pitch.ts`.
 *
 * See `studio/src/views/PitchLifecyclePanel.vue`,
 * `studio/src/views/PitchEdit.vue`, and
 * `studio/src/views/Dashboard.vue` for the call sites.
 */
export async function purgePitch(pitchId: string): Promise<void> {
  const row = (await databases.getDocument(
    databaseId,
    COLLS.pitches,
    pitchId,
  )) as Record<string, unknown>;
  const isDraft = canHardDeleteFromRow(row);
  const isArchived = isHardDeletableFromRow(row as { visibility?: string });
  if (!isDraft && !isArchived) {
    throw new Error("Pitch must be archived before it can be deleted.");
  }
  await databases.deleteDocument(databaseId, COLLS.pitches, pitchId);
}

/**
 * Promote a staged replacement via pitch-transition.
 * @deprecated prefer promotePitchReplacement — kept for one release.
 */
export async function promoteReplacement(
  replacementId: string,
  predecessorId: string,
) {
  return promotePitchReplacement(replacementId, predecessorId);
}

/**
 * Enqueue a static rebuild for a pitch slug (build_jobs).
 *
 * 2026-06-17: also accepts the `creator-republish` action for
 * per-creator /@<handle>/ page rebuilds. The signature is
 * unchanged for callers passing `(pitchId, slug, action)`;
 * creator-republish callers should prefer the dedicated
 * `enqueueCreatorPageJob` helper which sets `creatorId` and
 * `oldHandle` correctly.
 */
export async function enqueueBuildJob(
  pitchId: string,
  slug: string,
  action: "publish" | "expire" | "delete" | "creator-republish" = "publish"
) {
  return databases.createDocument(databaseId, COLLS.build_jobs, ID.unique(), {
    pitchId,
    slug,
    action,
    status: "pending",
    enqueuedAt: new Date().toISOString(),
  });
}

// ─── Pitch listings ───────────────────────────────────────────────────────────

export async function createListing(data: Record<string, unknown>) {
  return databases.createDocument(
    databaseId,
    COLLS.pitch_listings,
    ID.unique(),
    data,
    [Permission.read(Role.user(data["creatorId"] as string))]
  );
}

export async function getActiveListingForPitch(pitchId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.pitch_listings, [
    Query.equal("pitchId", pitchId),
    Query.equal("status", "active"),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function listListingsForCreator(creatorId: string) {
  return databases.listDocuments(databaseId, COLLS.pitch_listings, [
    Query.equal("creatorId", creatorId),
    Query.orderDesc("paidAt"),
    Query.limit(50),
  ]);
}

export async function updateListing(
  listingId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.pitch_listings, listingId, data);
}

// ─── Disclosures ──────────────────────────────────────────────────────────────

export async function listDisclosures(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.pitch_disclosures, [
    Query.equal("pitchId", pitchId),
  ]);
}

export async function createDisclosure(
  pitchId: string,
  data: Record<string, unknown>
) {
  // DATA-3 (2026-06-17): pitch_disclosures.creatorId is now a required
  // column. Copy it from the pitch row so the create succeeds.
  //
  // rowSecurity (2026-06-17): the table has `rowSecurity: true` so the
  // per-row permission array (the 5th arg of `createDocument`) is the
  // ONLY way the creator can read/update/delete the row they just
  // wrote. The Appwrite Permission role needs `Role.user(<users.$id>)`,
  // not `Role.user(<creators.$id>)`, so we resolve the creators row
  // by its $id (= pitch.creatorId) and read its `userId`.
  const pitch = await getPitch(pitchId);
  const creatorId = String((pitch as Record<string, unknown>)?.creatorId ?? "");
  if (!creatorId) {
    throw new Error(
      `createDisclosure: pitch ${pitchId} has no creatorId; cannot populate pitch_disclosures.creatorId (DATA-3).`,
    );
  }
  const creator = await getCreatorRowByCreatorId(creatorId);
  const userId = String((creator as Record<string, unknown>)?.userId ?? "");
  if (!userId) {
    throw new Error(
      `createDisclosure: no userId for creatorId ${creatorId}; cannot construct per-row permissions (rowSecurity).`,
    );
  }
  return databases.createDocument(
    databaseId,
    COLLS.pitch_disclosures,
    ID.unique(),
    { creatorId, pitchId, ...data },
    [
      Permission.read(Role.user(userId)),
      Permission.update(Role.user(userId)),
      Permission.delete(Role.user(userId)),
    ],
  );
}

export async function updateDisclosure(
  disclosureId: string,
  data: Record<string, unknown>,
) {
  return databases.updateDocument(databaseId, COLLS.pitch_disclosures, disclosureId, data);
}

/** First disclosure row for a pitch, if any. */
export async function getDisclosureForPitch(pitchId: string) {
  const res = await listDisclosures(pitchId);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function deleteDisclosure(disclosureId: string) {
  return databases.deleteDocument(databaseId, COLLS.pitch_disclosures, disclosureId);
}

// ─── Audience questions (pitcher-side editor) ─────────────────────────────

export interface AudienceQuestionRowApp {
  $id: string;
  pitchId: string;
  order: number;
  // v2 (audience-feedback v2, 2026-06-17): kind enum widened from
  // 3 to 7 values; 4 optional columns added to the underlying table.
  // v3 (2026-06-24): added `single_select_with_other` — the
  // "preset chips + Anything else? textarea" kind.
  kind: "yes_no" | "multiple_choice" | "five_face" | "single_select" | "multi_select" | "scale_1_5" | "short_text" | "single_select_with_other";
  templateKey: string | null;
  ratingDimKey: string | null;
  prompt: string;
  options: string | null;
  anchorKind: "on_section_active" | "start_of_segment" | "end_of_segment" | "timed_cue";
  anchorSectionId: string;
  anchorSeconds: string | null;
  allowExpansion: boolean;
  jumpUrl: string | null;
  status: "draft" | "live" | "paused" | null;
  audienceVariant: string | null;
  scaleMinLabel: string | null;
  scaleMaxLabel: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PitchSectionRowApp {
  $id: string;
  pitchId: string;
  order: number;
  kind: string;
  cues: string | null;
  body: string;
  title: string;
  // 2026-06-29 (PR 5 / relational migration): the studio's
  // `PitchEditShell` now fetches `pitch_sections` rows directly to
  // hydrate the section editor. The interface matches the
  // `pitch_sections` table schema (appwrite.config.json lines
  // 4134-4286) — every column the editor reads is named here so
  // the row carries headline / sub / mediaIds / video fields and
  // the editor can render them without a follow-up fetch. The
  // legacy interface omitted these fields; the audience-feedback
  // tab doesn't need them so the omission went unnoticed until
  // the section editor started showing empty content.
  headline: string | null;
  sub: string | null;
  mediaIds: string[] | null;
  videoSource: string | null;
  videoExternalUrl: string | null;
  bunnyVideoId: string | null;
}

/**
 * Fetch the pitch's `pitch_sections` and `pitch_audience_questions`
 * rows in parallel. The studio uses this for the new "Audience
 * feedback" tab; the Vue component groups the questions by section
 * and surfaces the editor-warn overflow signal.
 */
export async function listAudienceQuestions(pitchId: string): Promise<{
  sections: PitchSectionRowApp[];
  questions: AudienceQuestionRowApp[];
}> {
  const [sRes, qRes] = await Promise.all([
    databases.listDocuments(databaseId, COLLS.pitch_sections, [
      Query.equal("pitchId", pitchId),
      Query.orderAsc("order"),
      Query.limit(200),
    ]),
    databases.listDocuments(databaseId, COLLS.pitch_audience_questions, [
      Query.equal("pitchId", pitchId),
      Query.orderAsc("order"),
      Query.limit(200),
    ]),
  ]);
  return {
    sections: sRes.documents as unknown as PitchSectionRowApp[],
    questions: qRes.documents as unknown as AudienceQuestionRowApp[],
  };
}

/**
 * 2026-06-29 (PR 5 section-editor regression fix): fetch the
 * `pitch_sections` relational rows for a single pitch. The studio
 * needs this to hydrate the section editor on `/pitches/:id/edit`
 * because the parent `pitches` row no longer carries section
 * content as a JSON column (PR 5 moved it to the relational table
 * — see `pitch-write/src/handler.ts` "PR 5" comments).
 *
 * Returns the rows sorted by `order` ASC, matching the order the
 * public page renders. Returns `[]` (not throws) when no rows
 * exist — the wizard's `pitchToWizardFormState` falls back to the
 * framework's suggested sections in that case, and a 500 would
 * break the editor for legitimately-empty drafts.
 */
export async function listPitchSections(pitchId: string): Promise<PitchSectionRowApp[]> {
  if (!pitchId || typeof pitchId !== "string") return [];
  try {
    const res = await databases.listDocuments(databaseId, COLLS.pitch_sections, [
      Query.equal("pitchId", pitchId),
      Query.orderAsc("order"),
      Query.limit(200),
    ]);
    return (res.documents as unknown as PitchSectionRowApp[]) ?? [];
  } catch {
    // Non-fatal: the wizard's `pitchToWizardFormState` falls back
    // to the framework's suggested sections when no relational
    // rows are reachable. A 500 here would surface as a broken
    // edit page for an empty draft — unacceptable.
    return [];
  }
}

export interface UpsertAudienceQuestionInput {
  order: number;
  // v2 (audience-feedback v2, 2026-06-17): kind enum widened from
  // 3 to 7 values; 4 optional columns added to the underlying table.
  // v3 (2026-06-24): added `single_select_with_other`.
  kind: "yes_no" | "multiple_choice" | "five_face" | "single_select" | "multi_select" | "scale_1_5" | "short_text" | "single_select_with_other";
  templateKey?: string | null;
  ratingDimKey?: string | null;
  prompt: string;
  options?: string | null;
  anchorKind: "on_section_active" | "start_of_segment" | "end_of_segment" | "timed_cue";
  anchorSectionId: string;
  anchorSeconds?: string | null;
  allowExpansion: boolean;
  jumpUrl?: string | null;
  status?: "draft" | "live" | "paused" | null;
  audienceVariant?: string | null;
  scaleMinLabel?: string | null;
  scaleMaxLabel?: string | null;
}

/**
 * Pitcher-side upsert. Goes through the `audience-feedback-write`
 * Appwrite Function (NOT a direct `databases.createDocument`) so the
 * 1.9.5 dance scopes (rows/tables/documents) and the audit log fire
 * server-side. The function returns the canonical row, including
 * the Appwrite `$id`.
 */
export async function upsertAudienceQuestion(
  pitchId: string,
  input: UpsertAudienceQuestionInput,
): Promise<AudienceQuestionRowApp> {
  const result = await invokeFunction(
    AUDIENCE_FEEDBACK_WRITE_FUNCTION_ID,
    { pitchId, ...input },
    "/questions",
  );
  return result as unknown as AudienceQuestionRowApp;
}

export async function deleteAudienceQuestion(questionId: string): Promise<void> {
  // The plan's 2026-06-14 contract is that the function exposes
  // POST /questions (upsert) and POST /answers (record). Delete
  // currently is a direct table call from the studio (no soft
  // delete; the question is gone). If we add a soft-delete in v2,
  // it'll go through the function too.
  await databases.deleteDocument(
    databaseId,
    COLLS.pitch_audience_questions,
    questionId,
  );
}

// ─── Audience answers (read-side, pitcher dashboard) ────────────────────

/**
 * The shape of the `/aggregate?pitchId=X` response from the
 * `audience-feedback-public` Appwrite Function. Mirrors the
 * `AggregatePitchResult` type in the function's handler.ts
 * (the function is the source of truth — re-typed here as a
 * read-side convenience).
 */
export interface AudienceAggregate {
  ok: true;
  pitchId: string;
  questions: Record<
    string,
    {
      count: number;
      distribution: Record<string, number>;
      topAnswer: string | null;
      freeTextExcerpts: string[];
    }
  >;
  totalAnswers: number;
  dropOffTimeline: Array<{
    questionId: string;
    positionKind: "video_seconds" | "section_index";
    positionValue: number;
    positionSectionId: string;
  }>;
}

export async function getAudienceAggregate(pitchId: string): Promise<AudienceAggregate> {
  const result = await invokeFunction(
    AUDIENCE_FEEDBACK_PUBLIC_FUNCTION_ID,
    { pitchId },
    "/aggregate",
    ExecutionMethod.GET,
  );
  return result as unknown as AudienceAggregate;
}

// ─── Pitch rating (creator dashboard "My Pitches" list) ─────────────

/**
 * 2026-06-28 (studio dashboard aggregate): one row of the
 * HonestScore display on the studio dashboard's "My Pitches"
 * list. Mirrors the wire shape returned by
 * `pitch-rate/src/handler.ts#handleGetAggregate`:
 *
 *   - `honestScore: number | null` —
 *       `null` means "no critic has rated this pitch yet" (the
 *       dashboard must render "No ratings yet", NOT "0.0"). The
 *       shape is byte-identical to `AggregateResult` in
 *       `@honest-pitches/honestscore-core#aggregate.ts`; the
 *       studio renders it directly without re-coercing.
 *   - `criticCount: number` — number of distinct critics who
 *       have submitted a rating (each critic contributes exactly
 *       one row to the materialized view).
 *
 * The studio calls `getPitchRating(pitchId)` per row in the
 * dashboard list, in parallel via `Promise.all`. Failures are
 * swallowed (render "No ratings yet" rather than crash the row).
 */
export interface PitchRating {
  honestScore: number | null;
  criticCount: number;
}

/**
 * Read the HonestScore + critic count for a single pitch via
 * the `pitch-rate` function's owner-gated `GET /aggregate/:pitchId`
 * endpoint. Returns `null` on any failure (404, 403 not-the-owner,
 * network down) so the dashboard can render "No ratings yet" for
 * every pitch that has no aggregate, without a separate network
 * path. Throws only on syntactically malformed responses (which
 * are programmer errors, not runtime conditions).
 */
async function getPitchRatingFromFunction(
  pitchId: string,
): Promise<PitchRating | null | undefined> {
  try {
    const result = await invokeFunction(
      PITCH_RATE_FUNCTION_ID,
      {},
      `/aggregate/${encodeURIComponent(pitchId)}`,
      ExecutionMethod.GET,
    );
    const body = result as Partial<PitchRating> & { ok?: boolean };
    if (body.ok !== true) return undefined;
    if (typeof body.criticCount !== "number") return undefined;
    const score = body.honestScore;
    if (score !== null && typeof score !== "number") return undefined;
    return {
      criticCount: body.criticCount,
      honestScore: score ?? null,
    };
  } catch {
    return undefined;
  }
}

/**
 * Direct TablesDB read when `pitch-rate` is unavailable (executor
 * timeout, deploy drift, etc.). The ratings tables are
 * `read("any")` — the dashboard only queries pitchIds the creator
 * already owns from `listPitchesForCreator`, so this path does not
 * widen the data surface beyond the owner-gated function contract.
 */
export async function getPitchRatingFromSdk(
  pitchId: string,
): Promise<PitchRating | null> {
  if (!pitchId || typeof pitchId !== "string") return null;
  try {
    const cacheRes = await databases.listDocuments(
      databaseId,
      COLLS.ratings_honestscore_cache,
      [Query.equal("pitchId", pitchId), Query.limit(1)],
    );
    if (cacheRes.documents.length > 0) {
      const row = cacheRes.documents[0] as Record<string, unknown>;
      const honestScore =
        typeof row.honestScore === "number" ? row.honestScore : null;
      const criticCount =
        typeof row.criticCount === "number" ? row.criticCount : 0;
      return { honestScore, criticCount };
    }

    const ratingsRes = await databases.listDocuments(
      databaseId,
      COLLS.ratings_honestscore,
      [Query.equal("pitchId", pitchId), Query.limit(500)],
    );
    const { honestScore, criticCount } = aggregateFromRatings(
      ratingsRes.documents,
      500,
    );
    return { honestScore, criticCount };
  } catch {
    return null;
  }
}

export async function getPitchRating(
  pitchId: string,
): Promise<PitchRating | null> {
  if (!pitchId || typeof pitchId !== "string") return null;
  const fromFunction = await getPitchRatingFromFunction(pitchId);
  if (fromFunction !== undefined) return fromFunction;
  return getPitchRatingFromSdk(pitchId);
}

/**
 * Bulk form of `getPitchRating`. Calls `getPitchRating` for each
 * pitch in parallel, returns a `Map<pitchId, PitchRating>` for
 * the successful ones (omits `null` reads). The dashboard calls
 * this once on initial render; subsequent renders reuse the
 * existing map. Failures are silently dropped — same contract
 * as the single-pitch form.
 */
export async function getPitchRatings(
  pitchIds: readonly string[],
): Promise<Map<string, PitchRating>> {
  if (!Array.isArray(pitchIds) || pitchIds.length === 0) return new Map();
  const pairs = await Promise.all(
    pitchIds.map(async (id) => {
      const rating = await getPitchRating(id);
      return [
        id,
        rating ?? { honestScore: null, criticCount: 0 },
      ] as const;
    }),
  );
  return new Map(pairs);
}

// ─── Pitch Sharing Zone (2026-06-16) ──────────────────────────────────────

/** Aggregate view of share events for a single pitch. Returned by
 *  `pitch-share-write` `GET /aggregate?pitchId=X`. */
export interface ShareAggregate {
  pitchId?: string;
  total: number;
  byChannel?: Record<string, number>;
  byDay?: Array<{ day: string; count: number }>;
  topReferrers?: Array<{ referrer: string; count: number }>;
  lastSharedAt: string | null;
}

/** Read the share-event aggregate for a pitch (pitcher dashboard panel). */
export async function getShareAggregate(pitchId: string): Promise<ShareAggregate> {
  const result = await invokeFunction(
    PITCH_SHARE_WRITE_FUNCTION_ID,
    { pitchId },
    "/aggregate",
    ExecutionMethod.GET,
  );
  return result as unknown as ShareAggregate;
}

/** Record a single share event. Called by the visitor-side ShareWidget. */
export async function recordShareEvent(input: {
  pitchId: string;
  channel: string;
  sessionId?: string;
  referrer?: string;
  metadata?: Record<string, unknown>;
}): Promise<{ ok: true; eventId: string } | { ok: false; error: string }> {
  const result = await invokeFunction(
    PITCH_SHARE_WRITE_FUNCTION_ID,
    { ...input },
    "/events",
    ExecutionMethod.POST,
  );
  return result as { ok: true; eventId: string } | { ok: false; error: string };
}

/** Trigger a regenerate of the share-promo image for a given pitch + variant. */
export async function regenerateSharePromo(
  pitchId: string,
  variant: "square" | "vertical" = "square",
  forceRegenerate = false,
): Promise<{ ok: true; cdnUrl: string } | { ok: false; error: string }> {
  const result = await invokeFunction(
    "pitch-og-generate",
    { pitchId, variant, forceRegenerate },
    "/share-promo",
    ExecutionMethod.POST,
  );
  return result as { ok: true; cdnUrl: string } | { ok: false; error: string };
}

/** Trigger a regenerate of the share QR code for a given pitch. */
export async function regenerateShareQr(
  pitchId: string,
): Promise<{ ok: true; cdnUrl: string } | { ok: false; error: string }> {
  const result = await invokeFunction(
    PITCH_SHARE_QR_FUNCTION_ID,
    { pitchId },
    "/",
    ExecutionMethod.POST,
  );
  return result as { ok: true; cdnUrl: string } | { ok: false; error: string };
}

/** Send a share email (one or more recipients) via the Mautic template. */
export async function sendShareEmail(
  pitchId: string,
  recipients: Array<{ email: string; name?: string }>,
  message?: string,
): Promise<{ ok: true; sendId: string; recipientCount: number } | { ok: false; error: string }> {
  const result = await invokeFunction(
    PITCH_SHARE_WRITE_FUNCTION_ID,
    { pitchId, recipients, message },
    "/email",
    ExecutionMethod.POST,
  );
  return result as { ok: true; sendId: string; recipientCount: number } | { ok: false; error: string };
}

/** Curated tips for a single pitch (read-only list). */
export async function listShareTips(pitchId: string): Promise<Array<{
  $id: string;
  channel: string;
  tip: string;
  order: number;
  enabled: boolean;
}>> {
  const result = await invokeFunction(
    PITCH_SHARE_WRITE_FUNCTION_ID,
    { pitchId },
    "/tips",
    ExecutionMethod.GET,
  );
  return (result as { tips: Array<{
    $id: string;
    channel: string;
    tip: string;
    order: number;
    enabled: boolean;
  }> }).tips ?? [];
}

// ─── Pros / Cons ─────────────────────────────────────────────────────────────

export async function listProsCons(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.pitch_pros_cons, [
    Query.equal("pitchId", pitchId),
  ]);
}

export async function createProCon(
  pitchId: string,
  data: Record<string, unknown>
) {
  // DATA-3 (2026-06-17): pitch_pros_cons.creatorId is now a required
  // column. See createDisclosure above for the rationale.
  //
  // rowSecurity (2026-06-17): per-row permissions for the creator
  // (see createDisclosure for the full pattern).
  const pitch = await getPitch(pitchId);
  const creatorId = String((pitch as Record<string, unknown>)?.creatorId ?? "");
  if (!creatorId) {
    throw new Error(
      `createProCon: pitch ${pitchId} has no creatorId; cannot populate pitch_pros_cons.creatorId (DATA-3).`,
    );
  }
  const creator = await getCreatorRowByCreatorId(creatorId);
  const userId = String((creator as Record<string, unknown>)?.userId ?? "");
  if (!userId) {
    throw new Error(
      `createProCon: no userId for creatorId ${creatorId}; cannot construct per-row permissions (rowSecurity).`,
    );
  }
  return databases.createDocument(
    databaseId,
    COLLS.pitch_pros_cons,
    ID.unique(),
    { creatorId, pitchId, ...data },
    [
      Permission.read(Role.user(userId)),
      Permission.update(Role.user(userId)),
      Permission.delete(Role.user(userId)),
    ],
  );
}

export async function deleteProCon(id: string) {
  return databases.deleteDocument(databaseId, COLLS.pitch_pros_cons, id);
}

// ─── Claims ─────────────────────────────────────────────────────────────────

export async function listClaims(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.claims, [
    Query.equal("pitchId", pitchId),
  ]);
}

export async function createClaim(pitchId: string, data: Record<string, unknown>) {
  // DATA-3 (2026-06-17): claims.creatorId is now a required column.
  // See createDisclosure above for the rationale.
  //
  // rowSecurity (2026-06-17): per-row permissions for the creator
  // (see createDisclosure for the full pattern).
  const pitch = await getPitch(pitchId);
  const creatorId = String((pitch as Record<string, unknown>)?.creatorId ?? "");
  if (!creatorId) {
    throw new Error(
      `createClaim: pitch ${pitchId} has no creatorId; cannot populate claims.creatorId (DATA-3).`,
    );
  }
  const creator = await getCreatorRowByCreatorId(creatorId);
  const userId = String((creator as Record<string, unknown>)?.userId ?? "");
  if (!userId) {
    throw new Error(
      `createClaim: no userId for creatorId ${creatorId}; cannot construct per-row permissions (rowSecurity).`,
    );
  }
  return databases.createDocument(
    databaseId,
    COLLS.claims,
    ID.unique(),
    { creatorId, pitchId, ...data },
    [
      Permission.read(Role.user(userId)),
      Permission.update(Role.user(userId)),
      Permission.delete(Role.user(userId)),
    ],
  );
}

export async function updateClaim(
  claimId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.claims, claimId, data);
}

// ─── Votes ───────────────────────────────────────────────────────────────────

export async function castVote(data: {
  pitchId: string;
  hypeScore: number;
  realityScore: number;
  anonHash?: string;
}) {
  return databases.createDocument(
    databaseId,
    COLLS.votes_hype_reality,
    ID.unique(),
    data
  );
}

export async function getVotesForPitch(pitchId: string) {
  return databases.listDocuments(databaseId, COLLS.votes_hype_reality, [
    Query.equal("pitchId", pitchId),
  ]);
}

// ─── Conversion events ───────────────────────────────────────────────────────

export async function logConversion(data: {
  pitchId: string;
  eventType: string;
  eventPositionSeconds?: number;
  valueCents?: number;
  referrer?: string;
  country?: string;
  device?: string;
}) {
  return databases.createDocument(
    databaseId,
    COLLS.conversion_events,
    ID.unique(),
    { ...data, createdAt: new Date().toISOString() }
  );
}

// ─── Flags ───────────────────────────────────────────────────────────────────

export async function createFlag(data: Record<string, unknown>) {
  return databases.createDocument(databaseId, COLLS.flags, ID.unique(), data);
}

// ─── Creators ────────────────────────────────────────────────────────────────

export async function getOrCreateCreator(userId: string, email: string) {
  const res = await databases.listDocuments(databaseId, COLLS.creators, [
    Query.equal("userId", userId),
    Query.limit(1),
  ]);
  if (res.documents[0]) return res.documents[0];

  return databases.createDocument(databaseId, COLLS.creators, ID.unique(), {
    userId,
    email,
    plan: "free",
  });
}

export async function getCreatorByUserId(userId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.creators, [
    Query.equal("userId", userId),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

/**
 * Reverse lookup: given a `creators.$id` (the value stored in
 * `pitch_disclosures.creatorId`, `pitch_pros_cons.creatorId`,
 * `claims.creatorId`, etc.), return the matching `creators` row.
 * Returns `null` if no row matches.
 *
 * Used by the per-row permission flow (see `rowSecurity` on
 * `appwrite.config.json`): the Appwrite Permission role needs
 * `Role.user(<users.$id>)` (the Appwrite auth user), not
 * `Role.user(<creators.$id>)` (the creator row id). So the create
 * helpers for tables that carry `creatorId` look up the
 * corresponding `creators` row and read its `userId` to construct
 * the per-row permission array.
 */
export async function getCreatorRowByCreatorId(creatorId: string) {
  if (!creatorId) return null;
  const res = await databases.listDocuments(databaseId, COLLS.creators, [
    Query.equal("$id", creatorId),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function updateCreator(
  creatorId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.creators, creatorId, data);
}

/**
 * Fetch a creator by its @handle. Returns null when not found.
 * The handle is lowercased + URL-normalized before the query so
 * callers can pass user-typed input safely. The Appwrite
 * `handle_unique` index (added 2026-06-17) makes this a single
 * indexed lookup rather than a list-then-find scan.
 */
export async function getCreatorByHandle(handle: string): Promise<Record<string, unknown> | null> {
  const normalized = normalizeHandle(handle);
  if (!normalized) return null;
  const res = await databases.listDocuments(databaseId, COLLS.creators, [
    Query.equal("handle", normalized),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

/**
 * Check whether a given handle is available. Returns:
 *   { available: true }  — no other creator has this handle.
 *   { available: false, takenBy: "<creatorId>" } — someone else has it.
 *   { available: false, takenBy: null } — owned by the same user
 *     (safe to keep when saving a self-update).
 *
 * The handle is lowercased + URL-normalized before the query. The
 * caller is expected to also gate on creatorId (the studio's own
 * user re-saving their profile should not be blocked by their
 * own handle).
 */
export async function checkHandleAvailable(
  handle: string,
  selfCreatorId?: string
): Promise<{ available: boolean; takenBy: string | null }> {
  const existing = await getCreatorByHandle(handle);
  if (!existing) return { available: true, takenBy: null };
  const takenBy = String(existing.$id ?? "");
  if (selfCreatorId && takenBy === selfCreatorId) {
    return { available: true, takenBy: null };
  }
  return { available: false, takenBy };
}

/**
 * Enqueue a per-creator /@<handle>/ page rebuild. Inserts a row into
 * the Appwrite `build_jobs` table with action="creator-republish" +
 * creatorId + (optional) oldHandle. The site-rebuild-service (SRS)
 * worker polls this table and dispatches the job to
 * www/scripts/render-creator.mjs.
 *
 * Pass `oldHandle` on a handle change so the worker also renders a
 * /@<oldHandle>/ redirect page alongside the new /@<handle>/ page.
 */
export async function enqueueCreatorPageJob(
  creatorId: string,
  handle: string,
  oldHandle?: string
) {
  return databases.createDocument(databaseId, COLLS.build_jobs, ID.unique(), {
    creatorId,
    slug: `@${handle}`,
    action: "creator-republish",
    status: "pending",
    enqueuedAt: new Date().toISOString(),
    ...(oldHandle ? { oldHandle } : {}),
  });
}

/**
 * Create a legacy_creator_redirects row. Called on handle change
 * BEFORE the creators.handle update so the redirect is in place
 * even if the update fails or is interrupted. The unique index on
 * `oldHandle` makes this idempotent — a duplicate write returns
 * the existing row.
 *
 * Returns the row's $id (existing or newly created).
 */
export async function createLegacyCreatorRedirect(args: {
  oldHandle: string;
  newHandle: string;
  creatorId: string;
}): Promise<string> {
  const existing = await databases.listDocuments(databaseId, COLLS.legacy_creator_redirects, [
    Query.equal("oldHandle", args.oldHandle),
    Query.limit(1),
  ]);
  if (existing.documents[0]) {
    return String(existing.documents[0].$id);
  }
  const created = await databases.createDocument(
    databaseId,
    COLLS.legacy_creator_redirects,
    ID.unique(),
    {
      oldHandle: args.oldHandle,
      newHandle: args.newHandle,
      creatorId: args.creatorId,
      createdAt: new Date().toISOString(),
    }
  );
  return String(created.$id);
}

/**
 * Parse the socialLinks JSON column from a creators row. Tolerates
 * corrupted JSON (returns []). Mirrors the server-side
 * `parseSocialLinks` in honest-pitches-web-public/src/_data/creators.js
 * so the studio UI and the public renderer agree on the shape.
 */
export function parseSocialLinks(raw: unknown): Array<{ platform: string; url: string }> {
  if (!raw) return [];
  if (typeof raw !== "string") return [];
  let arr: unknown;
  try {
    arr = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(arr)) return [];
  const out: Array<{ platform: string; url: string }> = [];
  for (const row of arr) {
    if (!row || typeof row !== "object") continue;
    const platform = typeof (row as any).platform === "string" ? (row as any).platform.trim() : "";
    const url = typeof (row as any).url === "string" ? (row as any).url.trim() : "";
    if (!platform || !url) continue;
    out.push({ platform, url });
  }
  return out;
}

/**
 * Stringify + validate the social links array for a save. Throws
 * on malformed input (so the studio can surface a useful error
 * before hitting the Appwrite validation). The output is suitable
 * for `creators.socialLinks` (a JSON string).
 */
export function stringifySocialLinks(links: Array<{ platform: string; url: string }>): string {
  if (!Array.isArray(links)) {
    throw new Error("socialLinks must be an array");
  }
  const out: Array<{ platform: string; url: string }> = [];
  for (let i = 0; i < links.length; i++) {
    const row = links[i];
    if (!row || typeof row !== "object") continue;
    const platform = String(row.platform ?? "").trim();
    const url = String(row.url ?? "").trim();
    if (!platform && !url) continue; // empty rows are silently dropped
    if (!platform) throw new Error(`socialLinks[${i}]: platform is required`);
    if (!url) throw new Error(`socialLinks[${i}]: url is required`);
    if (!/^https?:\/\//i.test(url)) {
      throw new Error(`socialLinks[${i}]: url must start with http:// or https://`);
    }
    out.push({ platform, url });
  }
  return JSON.stringify(out);
}

/**
 * Normalize a handle for URL use. Lowercase + URL-safe regex
 * (same shape as the public data file). Returns null for invalid
 * input. Exposed for the studio's handle-uniqueness check + the
 * Settings form's "available" hint.
 */
export function normalizeHandle(input: string | null | undefined): string | null {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (!/^[a-z0-9_-]{1,64}$/.test(lower)) return null;
  return lower;
}

// ─── Subscriptions ────────────────────────────────────────────────────────────

export async function getActiveSubscription(creatorId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.subscriptions, [
    Query.equal("creatorId", creatorId),
    Query.equal("status", "active"),
    Query.limit(1),
  ]);
  return (res.documents[0] as Record<string, unknown>) ?? null;
}

export async function createSubscription(data: Record<string, unknown>) {
  return databases.createDocument(databaseId, COLLS.subscriptions, ID.unique(), data);
}

export async function updateSubscription(
  subId: string,
  data: Record<string, unknown>
) {
  return databases.updateDocument(databaseId, COLLS.subscriptions, subId, data);
}

// ─── Storage ────────────────────────────────────────────────────────────────

/**
 * Bucket IDs. Kept here so the studio, the public site, and any future
 * server-side render can refer to buckets by constant instead of magic
 * strings — and so renaming a bucket is a one-file change.
 */
export const USER_MEDIA_BUCKET_ID = "user-media";

/**
 * Mirror of `appwrite.config.json#buckets[user-media].maximumFileSize`
 * (10485760 = 10 MB). The bucket is the hard server-side cap; we keep
 * this constant so the client-side preview cap can be derived against
 * it without reading the Appwrite config at runtime.
 */
export const USER_MEDIA_BUCKET_MAX_BYTES = 10 * 1024 * 1024;

/**
 * 2026-06-25 (unify_image_picker plan): friendly client-side cap for
 * avatar uploads. Half the bucket cap — leaves headroom for retries /
 * transformations, and a smaller upload is friendlier on metered
 * connections. The picker surfaces this in the "Max size" hint; the
 * server still enforces `USER_MEDIA_BUCKET_MAX_BYTES`.
 */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

/**
 * Allowed MIME types for avatar uploads. Mirrors
 * `appwrite.config.json#buckets[user-media].allowedFileExtensions`
 * (jpg, jpeg, png, webp, gif). GIF is permitted by the bucket but
 * discouraged for avatars (no animation benefit, large files);
 * the picker accepts it for backwards-compat with already-uploaded
 * avatars in the library.
 */
export const AVATAR_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

/**
 * Appwrite Storage `createFile` callback shape. The web SDK fires this
 * callback during chunked uploads (per the Appwrite SDK source at
 * https://github.com/appwrite/sdk-for-web/blob/master/src/client.ts#L162-L174):
 * `progress` is the percentage 0..100, NOT a fraction. We forward the
 * raw callback to the Appwrite SDK; consumers (e.g. MediaPickerModal)
 * can read `progress.progress` and divide by 100 to get the fraction
 * the rest of the studio uses.
 *
 * Per Appwrite SDK source, the `UploadProgress` shape is:
 *   `{ $id, progress (0..100), sizeUploaded, chunksTotal, chunksUploaded }`.
 */
export type UploadProgress = {
  $id: string;
  /** Percentage 0..100. */
  progress: number;
  sizeUploaded: number;
  chunksTotal: number;
  chunksUploaded: number;
};

export async function uploadFile(
  bucketId: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void,
) {
  // Appwrite Storage SDK signature: createFile(bucketId, fileId, file,
  // permissions?, onProgress?). The 5th arg is the upload progress
  // callback; per the SDK source the `progress` field is a percentage
  // 0..100, NOT a fraction. We forward unchanged so the caller can
  // decide how to render. MediaPickerModal divides by 100 to drive
  // the same `<progress>` UI as the pitch-asset presign branch.
  return storage.createFile(bucketId, ID.unique(), file, [], onProgress);
}

// ─── Avatar library (Appwrite Storage `user-media` bucket) ──────────

/**
 * 2026-06-25 (unify_image_picker plan): one row of the caller's
 * avatar history, as returned by the `my-avatars-list` Appwrite
 * Function. `id` is the Appwrite Storage file `$id` — the studio
 * persists this as `creators.avatarFileId` (unchanged from today).
 *
 * The shape mirrors what `getAvatarUrl` consumes (it takes the
 * `previewUrl` directly), so the studio can hand the `previewUrl`
 * straight to an `<img>` tag in the picker grid.
 */
export interface PublicAvatar {
  id: string;
  previewUrl: string;
  sizeBytes: number;
  mimeType: string;
  uploadedAt: string;
  name: string;
}

export interface ListMyAvatarsResponse {
  ok: true;
  avatars: PublicAvatar[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListMyAvatarsError {
  ok: false;
  status: number;
  error: string;
}

interface MyAvatarsExecutionsResponse<T> {
  responseBody?: string | T;
  $id?: string;
  status?: string;
  responseStatusCode?: number;
  errors?: string;
}

function unwrapMyAvatarsResponse<T>(wrapped: MyAvatarsExecutionsResponse<T>): T {
  const inner = wrapped.responseBody;
  if (typeof inner === "string") {
    try {
      return JSON.parse(inner) as T;
    } catch {
      throw new Error(
        `my-avatars-list responseBody was a string but not JSON: ${inner.slice(0, 200)}`,
      );
    }
  }
  if (inner && typeof inner === "object") return inner as T;
  return wrapped as unknown as T;
}

/**
 * GET the calling user's avatar library page from the `my-avatars-list`
 * Appwrite Function. Same direct-fetch + session-cookie pattern as
 * `MyVideosView.vue` and `src/lib/media.ts#listMyMedia`. Returns the
 * parsed `ListMyAvatarsResponse` (avatars + total + limit + offset).
 *
 * Throws on network / parsing errors; surfaces 4xx/5xx as `Error`.
 */
export async function listMyAvatars(args: {
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
} = {}): Promise<ListMyAvatarsResponse> {
  const endpoint = (import.meta.env.VITE_APPWRITE_FUNCTION_ENDPOINT as string | undefined) ?? "";
  const projectId = (import.meta.env.VITE_APPWRITE_PROJECT_ID as string | undefined) ?? "";
  if (!endpoint || !projectId) {
    throw new Error(
      "The avatar service is not configured for this build; please contact support.",
    );
  }
  const params = new URLSearchParams();
  params.set("limit", String(args.limit ?? 24));
  params.set("offset", String(args.offset ?? 0));
  const url = `${endpoint}/functions/my-avatars-list/executions?${params.toString()}`;
  const f = args.fetchImpl ?? fetch;
  const res = await f(url, {
    method: "GET",
    headers: { "X-Appwrite-Project": projectId },
    credentials: "include",
    signal: args.signal ?? null,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `my-avatars-list returned ${res.status}: ${text.slice(0, 200)}`,
    );
  }
  const wrapped = (await res.json()) as MyAvatarsExecutionsResponse<ListMyAvatarsResponse>;
  return unwrapMyAvatarsResponse(wrapped);
}

// ─── Pitch asset upload (per-pitch thumbnails, OG images, attachments) ────────

/**
 * 2026-06-10: Upload a per-pitch asset (thumbnail, OG image, etc.)
 * via the `pitch-asset-upload` Appwrite Function. Returns the
 * uploaded `pitch_assets` row, including the `id` that should be
 * stored on the pitch as `thumbnailFileId` / `ogImageFileId`.
 *
 * Server-side constraints (mime, size, isPublic) live in
 * `packages/pitch-asset-core` and are enforced inside the function.
 * The client-side 2 MB guard in the wizard is a friendly preview
 * to give an immediate error, not a security boundary.
 */
export interface UploadPitchAssetArgs {
  pitchId: string;
  kind: PitchAssetKind;
  file: File;
  isPublic?: boolean;
}

export interface UploadPitchAssetResult {
  id: string;
  cdnUrl: string;
  bunnyPath: string;
}

export async function uploadPitchAsset(
  args: UploadPitchAssetArgs,
): Promise<UploadPitchAssetResult> {
  const buf = new Uint8Array(await args.file.arrayBuffer());
  let binary = "";
  for (let i = 0; i < buf.byteLength; i++) {
    binary += String.fromCharCode(buf[i] as number);
  }
  const bytesBase64 =
    typeof btoa === "function"
      ? btoa(binary)
      : Buffer.from(buf).toString("base64");

  const resolved = resolveConsolidatedRoute(PITCH_ASSET_UPLOAD_FUNCTION_ID, "/");
  const execution = await functionsSvc.createExecution(
    resolved.functionId,
    JSON.stringify({
      pitchId: args.pitchId,
      kind: args.kind,
      mimeType: args.file.type,
      sizeBytes: args.file.size,
      isPublic: args.isPublic ?? true,
      bytesBase64,
    }),
    false,
    resolved.path,
    ExecutionMethod.POST,
    { "content-type": "application/json" },
  );

  if (execution.status === "failed") {
    throw new Error(
      (execution.responseBody as unknown as { error?: string } | null)?.error ??
        execution.errors ??
        "upload failed",
    );
  }
  if (execution.responseStatusCode >= 400) {
    const errorBody = safeJson(execution.responseBody) as
      | { error?: string }
      | null;
    throw new Error(
      errorBody?.error ??
        execution.errors ??
        `${PITCH_ASSET_UPLOAD_FUNCTION_ID} failed`,
    );
  }
  const body = (safeJson(execution.responseBody) ?? {}) as Partial<UploadPitchAssetResult>;
  if (!body.id || !body.cdnUrl) {
    throw new Error("upload response missing id or cdnUrl");
  }
  return {
    id: body.id,
    cdnUrl: body.cdnUrl,
    bunnyPath: body.bunnyPath ?? "",
  };
}

// ─── Pitch asset presign (three-step direct-to-Bunny flow) ─────────────────

/**
 * 2026-06-26: direct-to-Bunny upload for pitch assets, with real
 * browser progress. Replaces the `functions.createExecution()`-only
 * path used by `uploadPitchAsset()`. The flow:
 *
 *   1. `presignPitchAsset` — POST {pitchId, kind, mimeType, sizeBytes}
 *      to `pitch-asset-presign`. Server validates pitch ownership,
 *      plans the Bunny path, signs a short-lived PUT URL.
 *   2. Browser PUTs the file body to the returned URL via XHR. The
 *      `xhr.upload.onprogress` callback fires with `loaded/total`
 *      ratios → real percentage bar in the picker UI.
 *   3. `finalizePitchAsset` — POST {pitchId, kind, bunnyPath,
 *      sizeBytes, mimeType} to `pitch-asset-finalize`. Server probes
 *      Bunny to confirm the object landed, writes the `pitch_assets`
 *      row + audit log. Returns `{ id, cdnUrl, bunnyPath }`.
 *
 * The `onProgress` callback receives a fraction in [0, 1] and is
 * also called with the value `1` while the finalize hop is in
 * flight (the PUT itself is done by then, but the row write is
 * still happening — labelling the state honestly is the whole
 * point of the progress bar).
 */
export interface PresignPitchAssetResponse {
  url: string;
  bunnyPath: string;
  expiresAt: number;
  headers: Record<string, string>;
  maxBytes: number;
}

export interface UploadPitchAssetViaPresignArgs {
  pitchId: string;
  kind: PitchAssetKind;
  file: File;
  isPublic?: boolean;
  onProgress?: (pct: number) => void;
  /** Injectable for tests. Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

/** POST to pitch-asset-presign; returns the signed URL + path. */
export async function presignPitchAsset(args: {
  pitchId: string;
  kind: PitchAssetKind;
  mimeType: string;
  sizeBytes: number;
  isPublic: boolean;
}): Promise<PresignPitchAssetResponse> {
  // 2026-06-29 (operator-requested debug): log the request payload
  // so a `pitch not found` / `you do not own this pitch` / `Server
  // misconfigured` error can be bisected against what the client
  // actually sent. The same fields are echoed in the `invokeFunction`
  // error log on failure, but logging them up-front means a
  // successful presign still leaves a breadcrumb.
  // eslint-disable-next-line no-console
  console.log("[presignPitchAsset] request", {
    functionId: PITCH_ASSET_PRESIGN_FUNCTION_ID,
    pitchId: args.pitchId,
    kind: args.kind,
    mimeType: args.mimeType,
    sizeBytes: args.sizeBytes,
    isPublic: args.isPublic,
  });
  const body = await invokeFunction(PITCH_ASSET_PRESIGN_FUNCTION_ID, {
    pitchId: args.pitchId,
    kind: args.kind,
    mimeType: args.mimeType,
    sizeBytes: args.sizeBytes,
    isPublic: args.isPublic,
  });
  if (typeof body.url !== "string" || typeof body.bunnyPath !== "string") {
    throw new Error("pitch-asset-presign returned no url or bunnyPath");
  }
  return body as unknown as PresignPitchAssetResponse;
}

/** POST to pitch-asset-finalize; returns the inserted row. */
export async function finalizePitchAsset(args: {
  pitchId: string;
  kind: PitchAssetKind;
  bunnyPath: string;
  sizeBytes: number;
  mimeType: string;
  isPublic?: boolean;
}): Promise<UploadPitchAssetResult> {
  const body = await invokeFunction(PITCH_ASSET_FINALIZE_FUNCTION_ID, {
    pitchId: args.pitchId,
    kind: args.kind,
    bunnyPath: args.bunnyPath,
    sizeBytes: args.sizeBytes,
    mimeType: args.mimeType,
    isPublic: args.isPublic ?? true,
  });
  if (typeof body.id !== "string" || typeof body.cdnUrl !== "string") {
    throw new Error("pitch-asset-finalize returned no id or cdnUrl");
  }
  return {
    id: body.id,
    cdnUrl: body.cdnUrl,
    bunnyPath: typeof body.bunnyPath === "string" ? body.bunnyPath : args.bunnyPath,
  };
}

/**
 * PUT a file to a presigned URL with real progress. Uses
 * `XMLHttpRequest.upload.onprogress` because `fetch` cannot read
 * upload progress as of 2026 (per the MDN docs cited in the plan).
 *
 * Resolves on HTTP 2xx; rejects with the response status + body on
 * anything else.
 *
 * 2026-06-29: switched from S3 SigV4 presigning (which returned 403 on
 * this storage zone because S3 compat is a closed-preview toggle that
 * can't be enabled after zone creation) to Bunny's regular HTTP API
 * presign (URL + AccessKey header bundle). The `headers` arg carries
 * the `AccessKey` value (the storage-zone password); the URL is the
 * bearer token (single object, single verb, short-lived).
 */
function putWithProgress(
  url: string,
  file: File,
  onProgress: (pct: number) => void,
  headers: Record<string, string> = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    for (const [name, value] of Object.entries(headers)) {
      // The AccessKey is the only header the Bunny HTTP API requires;
      // additional headers are still sent as-is for forward-compat.
      xhr.setRequestHeader(name, value);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        onProgress(Math.min(1, e.loaded / e.total));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new Error(`PUT failed: ${xhr.status} ${xhr.responseText?.slice(0, 200) ?? ""}`));
      }
    };
    xhr.onerror = () => reject(new Error("PUT network error"));
    xhr.onabort = () => reject(new Error("PUT aborted"));
    xhr.send(file);
  });
}

/**
 * Upload a pitch asset via the three-step presign flow:
 *   presign → direct PUT with progress → finalize.
 *
 * The legacy `uploadPitchAsset` (single-hop base64 via
 * `pitch-asset-upload`) is retained for one deploy cycle as a
 * fallback for non-browser callers (CLI regen scripts) and is
 * deprecated for browser callers.
 */
export async function uploadPitchAssetViaPresign(
  args: UploadPitchAssetViaPresignArgs,
): Promise<UploadPitchAssetResult> {
  const onProgress = args.onProgress ?? (() => {});
  // 2026-06-29 (operator-requested debug): a generic "Upload failed:
  // pitch not found" gives no signal whether the failure happened in
  // presign (404 ownership), PUT to Bunny (network / 403 expired
  // signature), or finalize (500 server-side). Tag each hop so a
  // console breadcrumb can pinpoint the failing stage without
  // re-running with the network panel open.
  const hopTag = `[uploadPitchAssetViaPresign pitchId=${args.pitchId} kind=${args.kind}]`;
  // eslint-disable-next-line no-console
  console.log(`${hopTag} stage=presign`);
  const presigned = await presignPitchAsset({
    pitchId: args.pitchId,
    kind: args.kind,
    mimeType: args.file.type,
    sizeBytes: args.file.size,
    isPublic: args.isPublic ?? true,
  });
  // eslint-disable-next-line no-console
  console.log(`${hopTag} stage=put bunnyPath=${presigned.bunnyPath}`);
  await putWithProgress(presigned.url, args.file, onProgress, presigned.headers);
  // Mark "100%" briefly while the finalize hop runs — labelling the
  // state honestly is the whole point of the progress bar.
  onProgress(1);
  // eslint-disable-next-line no-console
  console.log(`${hopTag} stage=finalize`);
  return finalizePitchAsset({
    pitchId: args.pitchId,
    kind: args.kind,
    bunnyPath: presigned.bunnyPath,
    sizeBytes: args.file.size,
    mimeType: args.file.type,
    isPublic: args.isPublic ?? true,
  });
}

// ─── Pitch assets (read-side) ─────────────────────────────────────────────
//
// The studio reads `pitch_assets` rows to populate the media-library
// picker. Writes still go through the `pitch-asset-upload` function so
// the Bunny Storage PUT + the Appwrite row insert stay atomic
// server-side.

export interface PitchAssetRow {
  $id: string;
  pitchId: string;
  kind: PitchAssetKind;
  bunnyPath: string;
  cdnUrl: string;
  mimeType: string;
  sizeBytes: number;
  isPublic: boolean;
  uploadedAt?: string;
}

export async function listPitchAssets(
  pitchId: string,
  kind?: PitchAssetKind,
): Promise<PitchAssetRow[]> {
  const queries = [
    Query.equal("pitchId", pitchId),
    Query.orderDesc("uploadedAt"),
    Query.limit(100),
  ];
  if (kind) queries.splice(1, 0, Query.equal("kind", kind));
  const res = await databases.listDocuments(databaseId, COLLS.pitch_assets, queries);
  return (res.documents as unknown as PitchAssetRow[]) ?? [];
}

export async function getPitchAsset(assetId: string): Promise<PitchAssetRow | null> {
  try {
    const row = await databases.getDocument(
      databaseId,
      COLLS.pitch_assets,
      assetId,
    );
    return row as unknown as PitchAssetRow;
  } catch {
    return null;
  }
}

// ─── Pitch videos (Bunny Stream / TUS uploads) ─────────────────────────────
//
// 2026-06-29 (per-segment-video v1.1, plan §2.1 / §3.2): direct
// `pitch_videos` reads from the Studio. The existing hero-path
// picker (`MyVideosPickerModal`) routes through the `my-videos-list`
// Appwrite Function (which enforces `uploadedByUserId == me` +
// status filter server-side). The new per-section picker
// (`SectionVideoPickerModal`) reads the table directly so it can
// filter by `pitchId` for the "This pitch" tab without a backend
// round-trip extension. The server-side row security on `pitch_videos`
// still gates writes; reads are filtered by Appwrite's per-row
// `read("user:<userId>")` permission so a creator cannot read another
// creator's video row even via this direct SDK call.
//
// Pinned by:
//   - scripts/per-segment-video-pitch-videos-purpose.contract.test.mjs
//     (the schema diff — purpose / sectionId columns)
//   - SectionVideoPickerModal.test.ts (the UI contract on these reads)

/** Canonical `pitch_videos` row shape as seen by the studio. The
 *  table is created and seeded by `bunny-presign-upload` /
 *  `bunny-webhook`; the studio never writes to it directly. The
 *  optional `purpose` + `sectionId` columns were added in PR 1 of
 *  the per-segment-video v1.1 plan; rows uploaded before that
 *  commit will have `purpose: null` + `sectionId: null` (the
 *  Appwrite Function writes `purpose: "hero"` server-side as the
 *  default for legacy callers). */
export interface PitchVideoRow {
  $id: string;
  $createdAt?: string;
  $updatedAt?: string;
  pitchId: string;
  bunnyVideoId: string;
  cdnUrl: string;
  /** "ready" | "uploading" | "failed". The picker only shows
   *  `ready` rows. */
  status: string;
  /** Bunny-side duration, nullable for legacy / partial rows. */
  durationSeconds: number | null;
  title: string | null;
  /** Server-side resolution tier (`sd_720` | `hd_1080` | `uhd_4k`)
   *  the upload was transcoded at. Null for legacy / partial rows. */
  resolutionTier: string | null;
  /** 2026-06-29 PR 1 schema column. `null` for pre-v1.1 rows. */
  purpose: "hero" | "section" | null;
  /** 2026-06-29 PR 1 schema column. `null` for pre-v1.1 rows. */
  sectionId: string | null;
  uploadedByUserId: string;
}

/**
 * List `pitch_videos` rows by `uploadedByUserId` and optional
 * `pitchId` filter. Status defaults to `"ready"` so the picker
 * surfaces only transcoded rows; pass `"all"` to see uploading /
 * failed rows for an operator/debug surface.
 *
 * Returns an empty array on any failure (the picker's empty state
 * is the right UX for both "no rows" and "table missing" — neither
 * is a user-actionable failure). Errors are reported to Sentry via
 * the existing `reportError` wrapper so the operator can bisect.
 */
export async function listPitchVideos(
  uploadedByUserId: string,
  opts: { pitchId?: string; status?: "ready" | "uploading" | "failed" | "all" } = {},
): Promise<PitchVideoRow[]> {
  if (!uploadedByUserId) return [];
  // Status defaults to "ready" — the studio picker should only ever
  // surface completed videos, not in-flight uploads or failed rows.
  // Callers can opt into the broader set via `status: "all"` (debug
  // surface only — the picker never uses it).
  const effectiveStatus = opts.status ?? "ready";
  const queries = [
    Query.equal("uploadedByUserId", uploadedByUserId),
    Query.orderDesc("$createdAt"),
    Query.limit(100),
  ];
  if (opts.pitchId) queries.splice(1, 0, Query.equal("pitchId", opts.pitchId));
  if (effectiveStatus !== "all") {
    queries.splice(opts.pitchId ? 2 : 1, 0, Query.equal("status", effectiveStatus));
  }
  try {
    const res = await databases.listDocuments(databaseId, COLLS.pitch_videos, queries);
    return (res.documents as unknown as PitchVideoRow[]) ?? [];
  } catch (e) {
    reportError(e, {
      where: "listPitchVideos",
      uploadedByUserId,
      pitchId: opts.pitchId,
      status: opts.status,
    });
    return [];
  }
}

/**
 * Resolve a single `pitch_videos` row by `$id` (status-agnostic).
 *
 * 2026-07-01 mobile-save regression: the wizard's per-section
 * video preview block needs the `cdnUrl` of the row whose `$id`
 * is stored in `pitch_sections.bunnyVideoId`. The pre-existing
 * `listPitchVideos` helper filters on `status: "ready"` by
 * default, which drops freshly-uploaded rows. This resolver is
 * the status-agnostic counterpart — it returns whatever Appwrite
 * has for the row, regardless of `status`. The public renderer
 * (and the Bunny iframe's own UI) is the gate for playability;
 * the metadata write this function enables (`bunnyVideoId` →
 * `cdnUrl`) is safe at any status.
 *
 * Returns `null` on miss / 404 / 5xx (best-effort hydration).
 *
 * Implementation note: this is a thin wrapper around
 * `databases.getDocument(databaseId, COLLS.pitch_videos, id)`. The
 * row-level permission check happens server-side (the user can
 * only read rows whose `pitchId` they own, modulo shared-pitch
 * permissions). The studio treats a 404 the same as a missing
 * row — the preview block stays hidden until the user re-picks.
 */
export async function getPitchVideoById(
  pitchVideoId: string,
): Promise<PitchVideoRow | null> {
  if (!pitchVideoId) return null;
  try {
    const row = await databases.getDocument(
      databaseId,
      COLLS.pitch_videos,
      pitchVideoId,
    );
    return row as unknown as PitchVideoRow;
  } catch (e) {
    reportError(e, {
      where: "getPitchVideoById",
      pitchVideoId,
    });
    return null;
  }
}

export function getFilePreviewUrl(
  bucketId: string,
  fileId: string,
  width?: number,
  height?: number
) {
  const params = new URLSearchParams();
  params.set("project", import.meta.env.VITE_APPWRITE_PROJECT_ID as string);
  if (width) params.set("width", String(width));
  if (height) params.set("height", String(height));
  return `${import.meta.env.VITE_APPWRITE_ENDPOINT}/storage/buckets/${bucketId}/files/${fileId}/preview?${params.toString()}`;
}

/**
 * Resolves a stored avatar `fileId` to a public URL on the
 * `user-media` Appwrite bucket. The bucket is configured with
 * `read("any")` so the URL works without a session JWT. The optional
 * `size` argument requests a square preview in pixels — defaults to
 * 256 which is large enough for retina but still cached-friendly.
 */
export function getAvatarUrl(fileId: string | null | undefined, size = 256): string | null {
  if (!fileId) return null;
  return getFilePreviewUrl(USER_MEDIA_BUCKET_ID, fileId, size, size);
}

/**
 * Build the Appwrite Avatars service URL for a "user initials"
 * fallback avatar. The service (`GET /avatars/initials`) is
 * project-wide (no project query param) and is enabled via
 * `services.avatars: true` in `appwrite.config.json`. The server
 * returns a PNG with the user's initials on a deterministic
 * background color; the random theme is name-persisted, so the same
 * name always gets the same color.
 *
 * The endpoint is hit directly from the browser as a plain image
 * tag — no SDK call needed. We build the URL as a pure string so
 * the profile menu can render the `<img>` synchronously without a
 * per-render Promise.
 *
 * @param name  Full name to extract initials from. May contain
 *              spaces or punctuation — `URLSearchParams` handles the
 *              encoding. Defaults to the Appwrite user's name when
 *              empty (handled server-side).
 * @param size  Square dimension in pixels (0-2000 per the Appwrite
 *              docs). Defaults to 100.
 * @param background  Optional 6-char hex (no leading `#`). When
 *                    omitted, Appwrite picks the random per-name
 *                    theme.
 */
export function getAccountInitialsUrl(
  name: string,
  size = 100,
  background?: string,
): string {
  const endpoint = (import.meta.env.VITE_APPWRITE_ENDPOINT as string) ?? "http://localhost/v1";
  const params = new URLSearchParams();
  if (name) params.set("name", name);
  params.set("width", String(size));
  params.set("height", String(size));
  if (background) params.set("background", background);
  return `${endpoint}/avatars/initials?${params.toString()}`;
}

// ─── Audit log helper ────────────────────────────────────────────────────────
//
// 2026-07-01 (agents-tab 500 fix): the previous shape issued an
// `Query.or([Query.equal("metadata.userId", ...), Query.equal("metadata.creatorId", ...)])`
// to surface both the post-2026-06-11 rows and the pre-migration
// rows in one Appwrite-side query. Appwrite 1.9.5 self-hosted returns
// `500 general_unknown` for that query against the `audit_log` table —
// `metadata` is a stringified-JSON column (size: 4096, type: "string"),
// not a typed JSON-path index, so Appwrite cannot resolve the nested
// `metadata.userId` / `metadata.creatorId` paths. The studio's Agents
// tab (now at /activity, route name "activity") rendered "Server Error" verbatim
// because the wrapper had no try/catch.
//
// The fix: do the owner-match in JavaScript, not in Appwrite. The
// `audit_log` table is `read("any")` with no rowSecurity, and an active
// creator's audit volume is bounded (tens per session, not millions),
// so a list-then-filter is cheap. The pure filter logic lives in
// `./list-agent-activity.ts` and is pinned by
// `./list-agent-activity.test.ts`.

import {
  filterAgentActivityForUser,
  type AgentActivityRow,
} from "./list-agent-activity";

/**
 * Optional dependency injection seam for the audit-log list path.
 *
 * The default behavior reads `audit_log` via the Appwrite SDK +
 * resolves the creator row via `getCreatorByUserId`. Tests inject
 * stand-ins so they can pin the SDK call shape and the filter
 * semantics without monkey-patching the Appwrite SDK.
 *
 * Matches the project's existing pattern in
 * `./get-pitch-video-by-id.ts` (test seams via pure helpers).
 */
export interface ListAgentActivityDeps {
  /** Resolves the creator row for the signed-in user. */
  getCreatorByUserId: (userId: string) => Promise<unknown | null>;
  /** Lists `audit_log` rows. Defaults to a fetch-then-orderDesc pair. */
  listDocuments: (
    databaseId: string,
    tableId: string,
    queries: unknown[],
  ) => Promise<{ documents: unknown[]; total: number }>;
}

/**
 * Pitch audit rows for the signed-in creator (human or agent SDK path).
 *
 * Returns `{ documents, total }` — both the filtered rows (`documents`)
 * and the post-filter count (`total`), matching the shape the
 * Appwrite SDK returns so the calling Vue component doesn't have to
 * branch on the data source.
 *
 * Tests may pass `deps` to substitute the Appwrite SDK calls.
 */
export async function listAgentActivityForUser(
  userId: string,
  limit = 50,
  deps?: Partial<ListAgentActivityDeps>,
) {
  const resolveCreator =
    deps?.getCreatorByUserId ?? ((id: string) => getCreatorByUserId(id));
  const fetchRows =
    deps?.listDocuments ?? ((...args: [string, string, unknown[]]) =>
      databases.listDocuments(
        args[0],
        args[1],
        args[2] as Parameters<typeof databases.listDocuments>[2],
      ));

  const creator = await resolveCreator(userId);
  if (!creator) {
    return { documents: [], total: 0 };
  }
  const creatorRow = creator as { $id?: string };
  const creatorId = creatorRow.$id ?? "";
  if (!creatorId) {
    return { documents: [], total: 0 };
  }

  const FETCH_BUDGET = Math.max(limit * 4, 200);
  const res = await fetchRows(databaseId, COLLS.audit_log, [
    Query.orderDesc("$createdAt"),
    Query.limit(FETCH_BUDGET),
  ]);
  const allRows = (res.documents as unknown as AgentActivityRow[]) ?? [];
  const filtered = filterAgentActivityForUser(allRows, userId, creatorId);
  const documents = filtered.slice(0, limit);
  return { documents, total: documents.length };
}

export async function logAudit(
  action: string,
  targetType: string,
  targetId: string,
  metadata: Record<string, unknown> = {},
  actorUserId?: string
) {
  return databases.createDocument(databaseId, COLLS.audit_log, ID.unique(), {
    action,
    targetType,
    targetId,
    metadata,
    actorUserId: actorUserId ?? null,
    createdAt: new Date().toISOString(),
  });
}

// ─── Stripe webhook idempotency ─────────────────────────────────────────────

export async function isStripeEventProcessed(stripeEventId: string) {
  const res = await databases.listDocuments(databaseId, COLLS.stripe_events, [
    Query.equal("stripeEventId", stripeEventId),
    Query.limit(1),
  ]);
  return res.documents.length > 0;
}

export async function markStripeEventProcessed(stripeEventId: string) {
  return databases.createDocument(
    databaseId,
    COLLS.stripe_events,
    ID.unique(),
    {
      stripeEventId,
      processedAt: new Date().toISOString(),
    }
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pitcher API keys (Bearer hp_pk_…)
// ─────────────────────────────────────────────────────────────────────────────

export interface PitcherApiKeyRow {
  $id: string;
  keyPrefix: string;
  label: string;
  scopes: string[];
  expiresAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  lastUsedAt: string | null;
}

/**
 * Response body from `POST /create` on pitcher-api-key-issue. The
 * raw token is shown to the user exactly once — the caller MUST copy
 * it before letting the modal close, because the server has no way to
 * recover it after this function returns.
 */
export interface PitcherApiKeyCreateResponse {
  rawToken: string;
  keyId: string;
  keyPrefix: string;
  label: string;
  scopes: string[];
  expiresAt: number | null;
  createdAt: number;
}

export async function listApiKeys(): Promise<{ keys: PitcherApiKeyRow[] }> {
  return (await invokeFunction(
    PITCHER_API_KEY_ISSUE_FUNCTION_ID,
    {},
    "/list",
    ExecutionMethod.GET,
  )) as { keys: PitcherApiKeyRow[] };
}

export async function createApiKey(input: {
  label: string;
  scopes: string[];
  expiresAt?: string | null;
}): Promise<PitcherApiKeyCreateResponse> {
  return (await invokeFunction(
    PITCHER_API_KEY_ISSUE_FUNCTION_ID,
    {
      label: input.label,
      scopes: input.scopes,
      expiresAt: input.expiresAt ?? null,
    },
    "/create",
  )) as unknown as PitcherApiKeyCreateResponse;
}

export async function revokeApiKey(input: { keyId: string; reason?: string }): Promise<{
  keyId: string;
  revokedAt: number | string;
  idempotent?: boolean;
}> {
  return (await invokeFunction(
    PITCHER_API_KEY_ISSUE_FUNCTION_ID,
    { keyId: input.keyId, reason: input.reason },
    "/revoke",
  )) as { keyId: string; revokedAt: number | string; idempotent?: boolean };
}

export async function fetchApiKeyScopeCatalog(): Promise<{ scopes: string[] }> {
  return (await invokeFunction(
    PITCHER_API_KEY_ISSUE_FUNCTION_ID,
    {},
    "/scope-catalog",
    ExecutionMethod.GET,
  )) as { scopes: string[] };
}

// 2026-06-30: re-tag a freshly uploaded pitch_videos row with
// purpose="section" + sectionId. Used by SectionVideoPickerModal
// after a BunnyUploader upload completes. See attachSectionVideo.ts
// for the full docstring; we re-export here so callers can import
// from a single barrel like the other SDK helpers.
export {
  attachSectionVideo,
  PITCH_VIDEO_SECTION_ATTACH_FUNCTION_ID,
} from "./attachSectionVideo";
